import logger from "lib/log";
import moment from "moment"
import queryString from "query-string";
import {compressToUTF16, decompressFromUTF16} from "lz-string";

const API_DATA_LOCALSTORAGE_KEY_NAME = "app_data";
const LOCALSTORAGE_TEMPLATE = {
  "school_money": {
    "school": null,
    "student": null,
    "parent": null,
    "active_payment": null,
    "shop": null
  },
  "last_fetch_date": {
    "school_money": {
      "school": null,
      "student": null,
      "parent": null,
      "active_payment": null,
      "shop": null
    }
  },
  "user_details": {
    "mobile_number": getMobileNumber()
  }
};

function createLocalStore() {
  try {
    const storeData = compressToUTF16(JSON.stringify(LOCALSTORAGE_TEMPLATE));
    localStorage.setItem(API_DATA_LOCALSTORAGE_KEY_NAME, storeData);
  } catch (error) {
    logger.error("Unable to save state to local storage:", error);
  }
}

function getMobileNumber() {
  const urlArgs = queryString.parse(window.location.search);

  // Get mobile number from local env if url args doesnt have mobile number
  return urlArgs.mobile_number === undefined ? process.env.REACT_APP_MOBILE_NUMBER : urlArgs.mobile_number;
}

function getLocalStoreData() {
  return localStorage.getItem(API_DATA_LOCALSTORAGE_KEY_NAME);
}

function getData(productKey, dataKey) {

  const localStoreData = getLocalStoreData();
  // Check if we have data in local storage
  if(localStoreData === null) {
    return undefined;
  }

  try {
    const storeData = JSON.parse(decompressFromUTF16(localStoreData));
    if(storeData[productKey] === undefined){return undefined;}
    if(storeData[productKey][dataKey] === undefined){return undefined;}

    return storeData[productKey][dataKey];
  } catch (error) {
    logger.error("Unable to get state local store data:", error);
    return undefined;
  }
}

function saveLocalStoreData(productKey, dataKey, data) {
  // Check if we have data in local storage
  if(getLocalStoreData() === null) {
    createLocalStore();
  }

  let localStoreData = getLocalStoreData();
  localStoreData = JSON.parse(decompressFromUTF16(localStoreData));
  localStoreData[productKey][dataKey] = data;
  localStoreData["last_fetch_date"][productKey][dataKey] = moment().format();
  localStorage.setItem(API_DATA_LOCALSTORAGE_KEY_NAME, compressToUTF16(JSON.stringify(localStoreData)));

}

function getLastFetchDate(productKey, dataKey){
  // Check if we have data in local storage
  if(getLocalStoreData() === null) {
    return null;
  }

  const localStoreData = JSON.parse(decompressFromUTF16(getLocalStoreData()));
  return localStoreData["last_fetch_date"][productKey][dataKey];
}

function getSchoolMoneyLastFecthDate(dataKey) {
  return getLastFetchDate("school_money", dataKey);
}

export function saveSchoolMoneyLocalStoreData(dataKey, data) {
  saveLocalStoreData("school_money", dataKey, data);
}

export function getSchoolMoneyLocalStoreData(dataKey) {
  const lastFetchDate = getSchoolMoneyLastFecthDate(dataKey);

  if(lastFetchDate === null){return undefined;}

  switch (dataKey) {
    case "school":
      return getSchoolLocalStoreData("school_money", dataKey, lastFetchDate);
    case "student":
      return getStudentLocalStoreData("school_money", dataKey, lastFetchDate);
    case "parent":
      return getParentLocalStoreData("school_money", dataKey, lastFetchDate);
    case "active_payment":
      return getActivePaymentLocalStoreData("school_money", dataKey, lastFetchDate);
    case "shop":
      return getShopLocalStoreData("school_money", dataKey, lastFetchDate);
    default: return undefined;
  }
}

function getSchoolLocalStoreData(productKey, dataKey, lastFetchDate) {
  // School data should refresh once a day
  if(moment().diff(lastFetchDate, "days") >= 1) {
    return undefined;
  }

  return getData(productKey, dataKey);
}

function getStudentLocalStoreData(productKey, dataKey, lastFetchDate) {
  // Student data should refresh instantly
  if(moment().diff(lastFetchDate, "seconds") >= 1) {
    return undefined;
  }

  return getData(productKey, dataKey);
}

function getParentLocalStoreData(productKey, dataKey, lastFetchDate) {
  // Parent email address data should be updated once a week
  if(moment().diff(lastFetchDate, "days") >= 7) {
    return undefined;
  }

  return getData(productKey, dataKey);
}

function getActivePaymentLocalStoreData(productKey, dataKey, lastFetchDate) {
  // Payment data should be refreshed instantly
  if(moment().diff(lastFetchDate, "seconds") >= 1) {
    return undefined;
  }

  return getData(productKey, dataKey);
}

function getShopLocalStoreData(productKey, dataKey, lastFetchDate) {
  // Shop data should be refreshed instantly
  if(moment().diff(lastFetchDate, "seconds") >= 1) {
    return undefined;
  }

  return getData(productKey, dataKey);
}

function getUserDetails(dataKey) {
  try {
    if(getLocalStoreData() === null) {
      return undefined;
    }
    let localStoreData = JSON.parse(decompressFromUTF16(getLocalStoreData()));
    let createNewStore = false
    // Check if we have data in local storage
    if(localStoreData === null || localStoreData["user_details"] === undefined) {
      createNewStore = true;
    }

    if(!createNewStore && localStoreData["user_details"][dataKey] === undefined) {
      createNewStore = true;
    }

    if(createNewStore) {
      clearData();
      createLocalStore();
      localStoreData = JSON.parse(decompressFromUTF16(getLocalStoreData()));
    }
    return localStoreData["user_details"][dataKey];
  } catch (error) {
    logger.error("Unable to get state local store data:", error);
    return undefined;
  }
}

export function getLocalStoreMobileNumber() {
  return getUserDetails("mobile_number");
}

export function clearData() {
  localStorage.removeItem(API_DATA_LOCALSTORAGE_KEY_NAME);
}

// Recreate the local store if the structure has changed
export function validateLocalStoreStructure() {
  // Clear the old unused key in local store
  localStorage.removeItem("t2p_app_data");

  // Do nothing if local store is empty
  if(getLocalStoreData() === null) {
    return;
  }

  const localStoreData = JSON.parse(decompressFromUTF16(getLocalStoreData()));
  const localStoreTemplateKeys = Object.keys(LOCALSTORAGE_TEMPLATE).sort();
  const localStoreDatateKeys = Object.keys(localStoreData).sort();

  // If the keys don't match then clear the local store
  if(!doesObjectStructureMatch(LOCALSTORAGE_TEMPLATE, localStoreData)) {
    clearData();
    return;
  }

  parentLoop:
  for(let i = 0; i < localStoreTemplateKeys.length; i = i + 1) {
    const templateChildKeys = Object.keys(LOCALSTORAGE_TEMPLATE[localStoreTemplateKeys[i]]).sort();
    const dataChildKeys = Object.keys(localStoreData[localStoreDatateKeys[i]]).sort();

    for(let j = 0; j < templateChildKeys.length; j = j + 1) {
      if(LOCALSTORAGE_TEMPLATE[localStoreTemplateKeys[i]][templateChildKeys[j]] !== null &&
          typeof LOCALSTORAGE_TEMPLATE[localStoreTemplateKeys[i]][templateChildKeys[j]] === "object") {
        if(!doesObjectStructureMatch(LOCALSTORAGE_TEMPLATE[localStoreTemplateKeys[i]][templateChildKeys[j]], localStoreData[localStoreDatateKeys[i]][dataChildKeys[j]])) {
          clearData();
          break parentLoop;
        }
      }
    }
  }
}

function doesObjectStructureMatch(localStoreTemplate, localStoreData) {
  const dataKeys = Object.keys(localStoreData).sort();
  const templateChildKeys = Object.keys(localStoreTemplate).sort();
  if(dataKeys.join("") !== templateChildKeys.join("")) {
    return false;
  }
  return true;
}



// WEBPACK FOOTER //
// ./src/state/localStore.js